/*
  Cart share via link.

  - Encode: read /cart.js, pack each line as {i: variant_id, q: qty, p: properties}
    into a base64url payload, build `<origin>/cart#sc=<payload>`, copy to clipboard.
  - Decode: on the cart page, if the URL has #sc=, add those items to the cart one at a
    time (so one dead variant can't fail the whole batch), strip the hash, and reload.

  Carries arbitrary line item properties (e.g. "Plakettenfarbe: m13-gelb"), which native
  Shopify cart permalinks cannot. Self-contained; safe to remove with cart-tools.liquid
  and cart-tools.css. See docs/superpowers/specs/2026-06-24-cart-share-and-pdf-design.md
*/
(function () {
  'use strict';

  var root = document.querySelector('.cart-tools');
  if (!root) return;

  var cfg = {};
  try {
    var cfgEl = document.getElementById('cart-tools-config');
    if (cfgEl) cfg = JSON.parse(cfgEl.textContent);
  } catch (e) {
    cfg = {};
  }

  var cartUrl = cfg.cartUrl || '/cart';
  var cartJsUrl = cfg.cartJsUrl || '/cart.js';
  var cartAddUrl = cfg.cartAddUrl || '/cart/add.js';
  var t = cfg.strings || {};

  // Guard against absurd payloads (~ very large carts overflowing the URL).
  var MAX_PAYLOAD = 30000;
  var NOTICE_KEY = 'cartToolsNotice';

  // --- base64url helpers (unicode-safe) ---
  function encodePayload(obj) {
    var json = JSON.stringify(obj);
    var b64 = btoa(unescape(encodeURIComponent(json)));
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function decodePayload(str) {
    var b64 = str.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    var json = decodeURIComponent(escape(atob(b64)));
    return JSON.parse(json);
  }

  // --- toast ---
  function toast(message, isError) {
    var el = document.createElement('div');
    el.className = 'cart-tools__toast' + (isError ? ' cart-tools__toast--error' : '');
    el.setAttribute('role', 'status');
    el.textContent = message;
    document.body.appendChild(el);
    // force reflow so the transition runs
    void el.offsetWidth;
    el.classList.add('is-visible');
    setTimeout(function () {
      el.classList.remove('is-visible');
      setTimeout(function () {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, 300);
    }, 4000);
  }

  // --- SHARE: build link and copy ---
  function buildShareLink() {
    return fetch(cartJsUrl, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) {
          throw new Error('empty');
        }
        var items = cart.items.map(function (line) {
          var entry = { i: line.id, q: line.quantity };
          // line.properties: object of name->value; drop empties and _hidden keys.
          if (line.properties) {
            var props = {};
            var has = false;
            Object.keys(line.properties).forEach(function (key) {
              var val = line.properties[key];
              if (key.charAt(0) === '_') return;
              if (val === null || val === '' || val === undefined) return;
              props[key] = val;
              has = true;
            });
            if (has) entry.p = props;
          }
          return entry;
        });
        var payload = encodePayload(items);
        if (payload.length > MAX_PAYLOAD) {
          throw new Error('toolarge');
        }
        return window.location.origin + cartUrl + '#sc=' + payload;
      });
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return Promise.reject(new Error('noclipboard'));
  }

  function onShareClick(btn) {
    btn.disabled = true;
    buildShareLink()
      .then(function (link) {
        return copyToClipboard(link).then(
          function () {
            toast(t.shareCopied || 'Link in die Zwischenablage kopiert.');
          },
          function () {
            // Clipboard unavailable (e.g. non-HTTPS): let the user copy manually.
            window.prompt(t.shareManual || 'Link zum Teilen kopieren:', link);
          }
        );
      })
      .catch(function (err) {
        if (err.message === 'empty') {
          toast(t.shareEmpty || 'Der Warenkorb ist leer.', true);
        } else if (err.message === 'toolarge') {
          toast(t.shareTooLarge || 'Der Warenkorb ist zu groß zum Teilen per Link.', true);
        } else {
          toast(t.shareError || 'Der Link konnte nicht erstellt werden.', true);
        }
      })
      .then(function () {
        btn.disabled = false;
      });
  }

  // --- DECODE: add shared items to the cart ---
  function addItem(entry) {
    var body = { items: [{ id: entry.i, quantity: entry.q || 1 }] };
    if (entry.p) body.items[0].properties = entry.p;
    return fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.ok ? { ok: true } : { ok: false };
    }, function () {
      return { ok: false };
    });
  }

  function processSharedHash() {
    var match = /[#&]sc=([^&]+)/.exec(window.location.hash);
    if (!match) return;

    var items;
    try {
      items = decodePayload(match[1]);
    } catch (e) {
      return; // garbage hash: ignore silently
    }
    if (!Array.isArray(items) || items.length === 0) return;

    // Add sequentially so a single failure doesn't abort the rest.
    var skipped = 0;
    var chain = Promise.resolve();
    items.forEach(function (entry) {
      chain = chain.then(function () {
        return addItem(entry).then(function (res) {
          if (!res.ok) skipped += 1;
        });
      });
    });

    chain.then(function () {
      var added = items.length - skipped;
      var notice;
      if (added === 0) {
        notice = { type: 'error', text: t.openNoneAdded || 'Die geteilten Artikel sind nicht mehr verfügbar.' };
      } else if (skipped > 0) {
        notice = {
          type: 'info',
          text: (t.openSomeAdded || 'Artikel hinzugefügt. Nicht verfügbar: {n}.').replace('{n}', String(skipped)),
        };
      } else {
        notice = { type: 'info', text: t.openAllAdded || 'Die geteilten Artikel wurden Ihrem Warenkorb hinzugefügt.' };
      }
      try {
        sessionStorage.setItem(NOTICE_KEY, JSON.stringify(notice));
      } catch (e) {}

      // Strip the hash so reload doesn't reprocess, then refresh to show the cart.
      history.replaceState(null, '', window.location.pathname + window.location.search);
      window.location.reload();
    });
  }

  function showPendingNotice() {
    var raw;
    try {
      raw = sessionStorage.getItem(NOTICE_KEY);
      if (raw) sessionStorage.removeItem(NOTICE_KEY);
    } catch (e) {
      return;
    }
    if (!raw) return;
    try {
      var notice = JSON.parse(raw);
      toast(notice.text, notice.type === 'error');
    } catch (e) {}
  }

  // --- init ---
  var shareBtn = root.querySelector('[data-cart-share]');
  if (shareBtn) {
    shareBtn.addEventListener('click', function () {
      onShareClick(shareBtn);
    });
  }

  showPendingNotice();
  processSharedHash();
})();
